import HardBreak from "@tiptap/extension-hard-break";
import { ListKit } from "@tiptap/extension-list";
import Placeholder from "@tiptap/extension-placeholder";
import { TableKit } from "@tiptap/extension-table";
import StarterKit from "@tiptap/starter-kit";
import { Extension } from "@tiptap/core";
import type { AnyExtension } from "@tiptap/react";
import { Markdown } from "tiptap-markdown";

import { DEFAULT_LINK_ATTRS, NonInclusiveLink } from "./link-mark";
import { PublicIdLinkDecoration, type PublicIdLinkResolverRef } from "./public-id-link-decoration";
import { SlashCommand } from "./slash-command";

/** Empty-item Enter lifts out of the list (Linear/Notion); otherwise split. */
const ListEnterLift = Extension.create({
  name: "listEnterLift",
  priority: 1_100,
  addKeyboardShortcuts() {
    return {
      Enter: () => {
        if (!this.editor.isActive("listItem")) return false;
        return (
          this.editor.commands.splitListItem("listItem") ||
          this.editor.commands.liftListItem("listItem")
        );
      },
    };
  },
});

/** Chip markdown: emit `\n` instead of tiptap-markdown's `\\\n`. */
const SoftNewlineHardBreak = HardBreak.extend({
  addStorage() {
    return {
      markdown: {
        serialize(
          state: { write: (text: string) => void },
          node: { type: unknown },
          parent: { childCount: number; child: (index: number) => { type: unknown } },
          index: number,
        ) {
          for (let i = index + 1; i < parent.childCount; i++) {
            if (parent.child(i).type !== node.type) {
              state.write("\n");
              return;
            }
          }
        },
        parse: {},
      },
    };
  },
});

/** Swallows Enter so single-line surfaces (e.g. a task title) stay one block. */
const SingleLine = Extension.create({
  name: "singleLine",
  addKeyboardShortcuts() {
    return {
      Enter: () => true,
      "Shift-Enter": () => true,
      "Mod-Enter": () => true,
    };
  },
});

export interface BuildChipExtensionsOptions {
  chip?: AnyExtension;
  placeholder?: string;
  openLinksOnClick?: boolean;
  singleLine?: boolean;
  markdown?: boolean;
  plainText?: boolean;
  /**
   * Linear issue markdown: `*` lists, no autolink, copy as markdown, and
   * hard breaks stored as CommonMark `\\\n`. Chip docs leave this off.
   */
  linear?: boolean;
  linkHTMLAttributes?: Record<string, string>;
  publicIdLinkResolver?: PublicIdLinkResolverRef;
}

/**
 * Shared TipTap extension set for every multi-line prose surface.
 * Chip families pass `chip`; markdown-string surfaces omit it.
 */
export function buildChipExtensions(opts: BuildChipExtensionsOptions = {}): AnyExtension[] {
  const single = opts.singleLine ?? false;
  const plain = opts.plainText ?? false;
  const linear = opts.linear ?? false;
  const markdown = (opts.markdown ?? true) && !single && !plain;
  const stripBlocks = single || plain;
  const richBlocks = !stripBlocks;
  const softBreak = markdown && !linear;

  const extensions: AnyExtension[] = [
    StarterKit.configure({
      heading: stripBlocks ? false : { levels: [1, 2, 3] },
      ...(stripBlocks
        ? {
            bulletList: false,
            orderedList: false,
            listItem: false,
            blockquote: false,
            codeBlock: false,
            horizontalRule: false,
            ...(single ? { hardBreak: false } : {}),
            ...(plain
              ? {
                  bold: false,
                  italic: false,
                  strike: false,
                  code: false,
                }
              : {}),
          }
        : {
            bulletList: false,
            orderedList: false,
            listItem: false,
            listKeymap: false,
            ...(softBreak ? { hardBreak: false } : {}),
          }),
      link: false,
    }),
  ];

  if (richBlocks) {
    extensions.push(
      ListKit.configure({
        taskItem: { nested: true },
      }),
      ListEnterLift,
      TableKit.configure({
        table: { resizable: false },
      }),
      SlashCommand,
    );
  }

  extensions.push(
    NonInclusiveLink.configure({
      autolink: true,
      linkOnPaste: true,
      openOnClick: opts.openLinksOnClick ?? linear,
      defaultProtocol: "https",
      HTMLAttributes: opts.linkHTMLAttributes ?? DEFAULT_LINK_ATTRS,
    }),
  );

  if (markdown) {
    extensions.push(
      Markdown.configure({
        html: false,
        transformPastedText: true,
        transformCopiedText: linear,
        // A single newline is a line break, so agent-written drafts keep their header lines.
        breaks: true,
        linkify: !linear,
        tightLists: true,
        ...(linear ? { bulletListMarker: "*" } : {}),
      }),
    );
    if (softBreak) {
      extensions.push(SoftNewlineHardBreak);
    }
  }

  if (opts.chip) {
    extensions.push(opts.chip);
  }
  if (opts.placeholder !== undefined) {
    extensions.push(Placeholder.configure({ placeholder: opts.placeholder }));
  }
  if (single) {
    extensions.push(SingleLine);
  }
  if (opts.publicIdLinkResolver) {
    extensions.push(PublicIdLinkDecoration(opts.publicIdLinkResolver));
  }
  return extensions;
}
